// Performance Trajectory — types + selection (Phase 3, D3 SVG line chart).
//
// One team's xGF (green) vs xGA (red) over its last N matches, optionally as a
// cumulative rolling average. The chart geometry + D3 animation live in the
// component; the pure part is windowing the matches, computing the rolling
// average / y-max, and formatting the per-dot tooltip.

/** One match's expected goals for / against. */
export interface TrajPoint {
  xGF: number;
  xGA: number;
}
/** Metadata for a match, merged onto its point for tooltips. */
export interface TrajMatchInfo {
  home: string;
  away: string;
  score: string;
  date: string;
}
/** Trajectory xG series keyed by full team name. */
export type TrajectoryData = Record<string, TrajPoint[]>;
/** Trajectory match metadata keyed by full team name. */
export type TrajectoryMatchInfoData = Record<string, TrajMatchInfo[]>;

/** A windowed match = its xG point plus (when available) its metadata. */
export type TrajMatch = TrajPoint & Partial<TrajMatchInfo>;

/** Shaped trajectory: the plotted series, the raw series (for rolling tooltips),
 *  the windowed matches, the y-max and count, and whether rolling is on. */
export interface TrajectoryShape {
  /** Plotted xGF series (rolling average when `isRolling`, else raw). */
  xgfVals: number[];
  /** Plotted xGA series. */
  xgaVals: number[];
  /** Raw (non-rolling) xGF, kept so rolling tooltips can show the actual value. */
  rawXgf: number[];
  rawXga: number[];
  matches: TrajMatch[];
  /** Y-axis max (ceil of the largest plotted value). */
  maxY: number;
  /** Number of matches in the window. */
  n: number;
  isRolling: boolean;
}

/** Cumulative running average, each rounded to 2dp (legacy `rollingAvg`). */
export function trajRollingAvg(vals: number[]): number[] {
  let sum = 0;
  return vals.map((v, i) => parseFloat(((sum += v) / (i + 1)).toFixed(2)));
}

/**
 * Window a team's matches (last 5 / 10 / whole season), merge in the metadata,
 * and compute the plotted series + y-max. Returns null when there's no data.
 */
export function selectTrajectory(
  data: TrajectoryData,
  matchInfo: TrajectoryMatchInfoData,
  team: string,
  timeline: string | null | undefined,
  view: string | null | undefined
): TrajectoryShape | null {
  const all = data[team] ?? [];
  const allInfo = matchInfo[team] ?? [];
  const n =
    timeline === "Season" ? all.length : timeline === "10" ? 10 : 5;
  const startIdx = Math.max(0, all.length - n);
  const matches: TrajMatch[] = all
    .slice(-n)
    .map((m, i) => ({ ...m, ...(allInfo[startIdx + i] ?? {}) }));
  if (!matches.length) return null;

  const rawXgf = matches.map((m) => m.xGF);
  const rawXga = matches.map((m) => m.xGA);
  const isRolling = view === "Rolling";
  const xgfVals = isRolling ? trajRollingAvg(rawXgf) : rawXgf;
  const xgaVals = isRolling ? trajRollingAvg(rawXga) : rawXga;
  const maxY = Math.ceil(Math.max(...xgfVals, ...xgaVals));

  return {
    xgfVals,
    xgaVals,
    rawXgf,
    rawXga,
    matches,
    maxY,
    n: matches.length,
    isRolling,
  };
}

/**
 * Tooltip text for the dot at index `i` on the xGF/xGA series. Rolling mode shows
 * the rolling value + the actual; otherwise the match card + the match's value.
 * Matches the legacy `updateDots` tooltip format exactly.
 */
export function trajTooltip(
  shape: TrajectoryShape,
  stat: "xgf" | "xga",
  i: number
): string {
  const label = stat === "xgf" ? "xGF" : "xGA";
  const v = (stat === "xgf" ? shape.xgfVals : shape.xgaVals)[i] ?? 0;
  if (shape.isRolling) {
    const rawVal = (stat === "xgf" ? shape.rawXgf : shape.rawXga)[i] ?? 0;
    return `Rolling ${label}: ${v.toFixed(2)}\nActual: ${rawVal.toFixed(2)}`;
  }
  const m = shape.matches[i] ?? ({} as TrajMatch);
  const val = (stat === "xgf" ? m.xGF : m.xGA) ?? 0;
  return m.home
    ? `${m.home} v ${m.away}\n${m.score}\n${m.date}\n${label}: ${val.toFixed(2)}`
    : `${label}: ${val.toFixed(2)}`;
}
